/**
 * Block navigation edge helpers.
 *
 * AI structured output uses the simplified V2 Trigger/Route schema where
 * routes point via `nextScreenId`. Persisted StrategyBlock uses BlockEdge from
 * the strategy schema, where block-level routes point via `nextBlockId`.
 */

import { TASK_ROUTER_VARIABLE_ID } from './edgeBuilder';

export interface BlockEdgeLike {
  nextBlockId?: string;
  nextScreenId?: string;
  sourceScreenId?: string;
  sourceActionKey?: string;
  targetScreenId?: string;
  transitionKind?: string;
  conditions?: Array<Record<string, unknown>>;
  rule?: Array<Record<string, unknown>>;
}

export interface BlockActionLike {
  kind?: string;
  edges?: BlockEdgeLike[];
}

export interface StrategyBlockLike {
  id?: string;
  entityId?: string;
  blockType?: string;
  taskId?: string | null;
  order?: number;
  isFirstBlock?: boolean;
  isLaunchBlock?: boolean;
  action?: BlockActionLike;
}

export function getBlockEntityId(block: StrategyBlockLike): string | undefined {
  return block.entityId || block.id;
}

export function getBlockEdgeTarget(edge: BlockEdgeLike): string | undefined {
  return edge.nextBlockId || edge.nextScreenId;
}

function getEdgeConditions(edge?: BlockEdgeLike): Array<Record<string, unknown>> {
  if (Array.isArray(edge?.conditions)) return edge.conditions;
  if (Array.isArray(edge?.rule)) return edge.rule;
  return [];
}

export function createStrategySchemaBlockAction(
  targets: Array<{
    targetEntityId: string;
    sourceScreenId?: string;
    sourceActionKey?: string;
    targetScreenId?: string;
    transitionKind?: string;
    conditions?: Array<Record<string, unknown>>;
  }>,
  kind = 'advance',
): BlockActionLike {
  return {
    kind,
    edges: targets.map(({
      targetEntityId,
      sourceScreenId,
      sourceActionKey,
      targetScreenId,
      transitionKind,
      conditions,
    }) => ({
      nextBlockId: targetEntityId,
      sourceScreenId,
      sourceActionKey,
      targetScreenId,
      transitionKind: transitionKind || 'push',
      conditions: conditions ?? [],
    })),
  };
}

export function normalizeBlockActionToStrategySchema(action?: BlockActionLike): BlockActionLike {
  const edges = Array.isArray(action?.edges) ? action.edges : [];
  const normalizedEdges = edges
    .map((edge) => {
      const target = getBlockEdgeTarget(edge);
      if (!target) return null;

      return {
        nextBlockId: target,
        sourceScreenId: edge.sourceScreenId,
        sourceActionKey: edge.sourceActionKey,
        targetScreenId: edge.targetScreenId,
        transitionKind: edge.transitionKind || 'push',
        conditions: getEdgeConditions(edge),
      };
    })
    .filter((edge): edge is NonNullable<typeof edge> => edge !== null);

  return {
    kind: action?.kind || (normalizedEdges.length > 0 ? 'advance' : 'complete'),
    edges: normalizedEdges,
  };
}

function parseStepNumber(block: StrategyBlockLike): number {
  const id = getBlockEntityId(block) || '';
  const match = id.match(/_step_(\d+)$/) || id.match(/^step_(\d+)$/);
  if (match) return Number(match[1]);
  return block.order ?? Number.MAX_SAFE_INTEGER;
}

function findCommonBlock(
  blocks: StrategyBlockLike[],
  entityId: string,
  blockType: string,
): StrategyBlockLike | undefined {
  return blocks.find((block) => getBlockEntityId(block) === entityId)
    || blocks.find((block) => block.blockType === blockType);
}

function inferTaskId(block: StrategyBlockLike, taskIds: string[]): string | null {
  if (block.taskId) return block.taskId;
  const id = getBlockEntityId(block) || '';
  return taskIds.find((taskId) => id.startsWith(`${taskId}_`)) ?? null;
}

export function normalizeStrategyBlockGraph(
  blocks: StrategyBlockLike[],
  taskIds: string[] = [],
): StrategyBlockLike[] {
  if (blocks.length === 0) return blocks;

  const welcome = findCommonBlock(blocks, 'step_01', 'welcome');
  const goals = findCommonBlock(blocks, 'step_02', 'user_goals');
  const goalsId = goals ? getBlockEntityId(goals) : undefined;

  for (const block of blocks) {
    block.entityId = getBlockEntityId(block);
    block.action = normalizeBlockActionToStrategySchema(block.action);
    // Common blocks (welcome, goals) are structural and shared by every task.
    // A generator-stamped taskId on them would pull them INTO a task chain:
    // the welcome block becomes the chain's "first block", the goals fork
    // targets it, and the funnel dead-ends (observed on single-task
    // strategies). Their taskId is always null.
    const isCommonBlock = block === welcome || block === goals;
    const inferredTaskId = isCommonBlock ? null : inferTaskId(block, taskIds);
    block.taskId = inferredTaskId ?? null;
    block.isFirstBlock = false;
    block.isLaunchBlock = false;
  }

  if (welcome) {
    welcome.isFirstBlock = true;
    welcome.isLaunchBlock = true;
    welcome.order = welcome.order ?? 0;
    if (goalsId) {
      welcome.action = createStrategySchemaBlockAction([{ targetEntityId: goalsId }]);
    }
  }

  if (goals) {
    goals.order = goals.order ?? 1;
  }

  const sortedTaskIds = taskIds.filter(Boolean);
  const firstBlocksByTask = new Map<string, StrategyBlockLike>();

  for (const taskId of sortedTaskIds) {
    const taskBlocks = blocks
      .filter((block) => inferTaskId(block, sortedTaskIds) === taskId)
      .sort((a, b) => parseStepNumber(a) - parseStepNumber(b));

    if (taskBlocks.length === 0) continue;
    firstBlocksByTask.set(taskId, taskBlocks[0]);

    taskBlocks.forEach((block, index) => {
      const next = taskBlocks[index + 1];
      const nextId = next ? getBlockEntityId(next) : undefined;
      block.action = nextId
        ? createStrategySchemaBlockAction([{ targetEntityId: nextId }])
        : createStrategySchemaBlockAction([], 'complete');
    });
  }

  if (goals) {
    const goalEdges = sortedTaskIds
      .map((taskId) => {
        const target = firstBlocksByTask.get(taskId);
        const targetEntityId = target ? getBlockEntityId(target) : undefined;
        if (!targetEntityId) return null;
        return {
          targetEntityId,
          conditions: [{ key: TASK_ROUTER_VARIABLE_ID, operator: 'eq', value: taskId }],
        };
      })
      .filter((edge): edge is NonNullable<typeof edge> => edge !== null);

    // B1-W4: unconditional DEFAULT edge to the first task chain. The runtime
    // evaluator sorts conditional routes first, so this only fires when no
    // condition matches — without it a missing/unmatched router value EXITS
    // the funnel (V2NavigationEngine no_matching_route).
    if (goalEdges.length > 0) {
      goalEdges.push({ targetEntityId: goalEdges[0].targetEntityId, conditions: [] });
    }

    goals.action = createStrategySchemaBlockAction(goalEdges, goalEdges.length > 0 ? 'advance' : 'complete');
  }

  return blocks;
}
